//#region Imports
import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Pressable, FlatList, SafeAreaView, Platform, useWindowDimensions } from 'react-native';
import { useTheme } from '@react-navigation/native';
import { createCommonStyles } from '../../../styles/commonStyles';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import { useTranslation } from 'react-i18next';
import { Card, Provider } from 'react-native-paper';
import CustomCalendar from '../../../components/CustomCalendar/CustomCalendar';
import CustomButton from '../../../components/CustomButton';
import { formatDateForUI } from '../../../constants/FormatDate';
import { useEvents } from '../../../hooks/useEvents';
import { showMessage } from "react-native-flash-message";

const MyRegistrations = ({ usersRegistrations, allEvents, setRefresh }) => {
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const { width, height } = useWindowDimensions();
    const styles = makeStyles(colors, width, height);
    const { t } = useTranslation();
    const { registerToEvent, cancelRegistrationToEvent } = useEvents();

    const [selectedData, setSelectedData] = useState([]);
    const [selectedDate, setSelectedDate] = useState([]);
    const [eventsOnSelectedDate, setEventsOnSelectedDate] = useState([]);

    useEffect(() => {
        if (!selectedData || !allEvents) {
            setEventsOnSelectedDate([]);
            return;
        }

        setSelectedData(selectedData)
        setEventsOnSelectedDate(selectedData);
    }, [selectedData, allEvents])

    const combinedEvents = React.useMemo(() => {
        const registeredEventUIs = !usersRegistrations ? new Set() : new Set(usersRegistrations.map(e => e.eventUI));
        const additionalEvents = eventsOnSelectedDate.filter(e => !registeredEventUIs.has(e.eventUI));

        if (!usersRegistrations || usersRegistrations.length === 0) {
            return [{title: 'header'}, {title: 'noRegistrations'}, {title: 'divider'}, ...additionalEvents];
        }
        return [{title: 'header'}, ...usersRegistrations, {title: 'divider'}, ...additionalEvents];
    }, [usersRegistrations, eventsOnSelectedDate]);

    //#region Rendering
    const renderItem = ({ item }) => {
        if (item.title === 'header') {
            return (
            <>
                <CustomLabel value="Registrations" type="SUBTITLE" />
                <View style={common.divider} />
            </>
            );
        }

        if (item.title === 'divider') {
            return (
            <>
                <CustomLabel value="Events of the Selected Day" type="SUBTITLE" />
                <View style={common.divider} />
            </>
            );
        }

        if (item.title === 'noRegistrations') {
            return (
                <View style={styles.marginhor}>
                    <CustomLabel
                        value={t('common:noRegistrations')}
                        type='MAIN_LABEL_TEAM_SCHEDULE' />
                </View>
            );
        }

        return (
            <Provider>
                <Card style={common.cardstyle}
                    onPress={() => onShowHidePress(item)} >
                    <Card.Content>
                        <ShowHideMenu {...item} />
                    </Card.Content>
                </Card >
            </Provider>
        )
        // }
    };

    const ShowHideMenu = (item) => {
        const [showDetails, setShowDetails] = React.useState(false);

        const onShowHidePress = (item) => {
            setShowDetails(!showDetails);
        };

        return (
            // * FlatList Main Data
            <View>
                <Pressable onPress={() => onShowHidePress(item)}>
                    <View style={styles.row}>
                        <CustomLabel
                            value={item.title}
                            type='AVAILABLEUSED' />
                        <CustomLabel
                            value={item.isActive ? 'Active' : 'Inactive'}
                            type='AVAILABLEUSED' />
                        <CustomLabel
                            value={item.isConfirmed ? 'Confirmed' : 'Not Confirmed'}
                            type='AVAILABLEUSED' />
                    </View>
                </Pressable>
                {showDetails == true &&
                    <SafeAreaView style={styles.showDetails}>
                        <SafeAreaView style={styles.item}>
                            <View style={styles.descriptionRow}>
                                <CustomLabel
                                    value={t('common:description') + `:`}
                                    type='LEAVEDETAILSMAJOR' />
                                <CustomLabel
                                    value={item.eventDescription}
                                    type='CARDDESCRIPTION' />
                            </View>
                            <View style={styles.line} />
                        </SafeAreaView>
                        <SafeAreaView style={styles.item}>
                            <View style={styles.descriptionRow}>
                                <CustomLabel
                                    value={t('common:session') + `:`}
                                    type='LEAVEDETAILSMAJOR' />
                                <CustomLabel
                                    value={item.sessionDescription}
                                    type='CARDDESCRIPTION' />
                            </View>
                            <View style={styles.line} />
                        </SafeAreaView>
                        <SafeAreaView style={styles.item}>
                            <View style={styles.descriptionRow}>
                                <CustomLabel
                                    value={t('common:startDate') + `:`}
                                    type='LEAVEDETAILSMAJOR' />
                                <CustomLabel
                                    value={formatDateForUI(item.sessionStartDate)}
                                    type='CARDDESCRIPTION' />
                            </View>
                            <View style={styles.line} />
                        </SafeAreaView>
                        <SafeAreaView style={styles.item}>
                            <View style={styles.descriptionRow}>
                                <CustomLabel
                                    value={t('common:endDate') + `:`}
                                    type='LEAVEDETAILSMAJOR' />
                                <CustomLabel
                                    value={formatDateForUI(item.sessionEndDate)}
                                    type='CARDDESCRIPTION' />
                            </View>
                            <View style={styles.line} />
                        </SafeAreaView>

                        {/* if not registered then appear buttons to register */}
                        {<View
                            style={{
                                height: 50,
                                flexDirection: 'row',
                                alignSelf: "center",
                            }}>
                            {item.isRegistered === 1 && <CustomButton
                                type="BUTTON_CANCEL"
                                text={t('common:cancelRegistretion')}
                                onPress={() => {
                                    cancelRegistrationToEvent(item.eventUI).then((res) => {
                                        if(res === 200){
                                            showMessage({
                                                message: t("common:cancelRegistrationSuccess"),
                                                type: "success",
                                                duration: 3000,
                                            });
                                            setRefresh(true)
                                        } else {
                                            showMessage({
                                                message: t("common:cancelRegistrationError"),
                                                type: "danger",
                                                duration: 3000,
                                            });
                                        }
                                    }).catch((err) => {
                                        showMessage({
                                            message: t("common:registrationError"),
                                            type: "danger",
                                            duration: 3000,
                                        });
                                    })
                                }}
                            />}
                            {item.isRegistered === 0 && <CustomButton
                            text={t('common:register')}
                            type="BUTTON_SAVE"
                            isGradient={true}
                            onPress={() => {
                                registerToEvent(item.eventUI).then((res) => {
                                    console.log(item.eventUI)
                                    console.log(res)
                                    if(res === 200){
                                        showMessage({
                                            message: t("common:registrationSuccess"),
                                            type: "success",
                                            duration: 3000,
                                        });
                                        setRefresh(true)
                                    } else {
                                        showMessage({
                                            message: t("common:registrationError"),
                                            type: "danger",
                                            duration: 3000,
                                        });
                                    }
                                }).catch((err) => {
                                    showMessage({
                                        message: t("common:registrationError"),
                                        type: "danger",
                                        duration: 3000,
                                    });
                                })
                            }}
                        />}
                        </View>}
                    </SafeAreaView>
                }
            </View>
        );
    };
    //#endregion

    return (
        <ScrollView style={styles.scroll}>
            <SafeAreaView style={styles.main}>
                <CustomLabel
                    value={t('navigate:MR')}
                    type='MINORTITLE'
                />
                <SafeAreaView style={styles.row}>
                    <SafeAreaView style={styles.calendar}>
                        <CustomCalendar
                            data={allEvents}
                            calendarType={'SelectableDate'}
                            setSelectedData={setSelectedData}
                            isEventsScreen={true}
                            isMyLeavesScreen={true}
                            selectedData={selectedData}
                            setCurrDate={null}
                            // set today as initial selected date to show event dots on load
                            selectedDate={new Date().toISOString().split('T')[0]}
                            key={allEvents?.length || 0} />
                    </SafeAreaView>

                    <SafeAreaView style={Platform.OS === 'web' && width > 1200 ? styles.eventNormal : styles.event}>
                        {combinedEvents && combinedEvents.length > 0 ? (
                            <FlatList
                                data={combinedEvents}
                                keyExtractor={(item, index) => item.eventUI || item.title || index.toString()}
                                renderItem={renderItem}
                                contentContainerStyle={{ paddingBottom: 20 }}
                            />
                        ) : (
                            <View style={styles.marginhor}>
                                <CustomLabel
                                    value={t('common:noEvents')}
                                    type='MAIN_LABEL_TEAM_SCHEDULE' />
                            </View>
                        )}
                    </SafeAreaView>
                </SafeAreaView>
            </SafeAreaView>
        </ScrollView>
    );
};

const makeStyles = (colors, width, height) =>
    StyleSheet.create({
        main: {
            flex: 1,
            marginTop: "1%",
            marginLeft: '2%',
            marginRight: '2%',
        },
        scroll: {
            flex: 1,
        },
        row: {
            flex: 1,
            flexDirection: width < 1200 ? 'column' : 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
        },
        descriptionRow: {
            flex: 1,
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            width: '100%',
        },
        calendar: {
            flex: 2,
            marginLeft: Platform.OS === 'web' ? '2%' : '1%',
            maxWidth: width * 0.3, // makes the leave card next to it shorter
            marginRight: Platform.OS === 'web' ? '2%' : '1%',
            marginTop: Platform.OS === 'web' ? '3%' : 0,
        },
        eventNormal: {
            flex: 1,
            marginTop: 10
            // height < 500 ? height * 40 / 100 : 10,
        },
        event: {
            width: width * 95 / 100,
            marginTop: height < 600 ? height * 40 / 100 : 10,
        },
        showHide: {
            borderRadius: 35,
            backgroundColor: colors.background_card,
        },
        showDetails: {
            marginVertical: 10,
        },
        item: {
            flex: 1,
        },
        line: {
            borderBottomColor: colors.green,
            borderBottomWidth: StyleSheet.hairlineWidth,
            marginTop: (Platform.OS === 'web') ? 10 : 20,
            marginBottom: (Platform.OS === 'web') ? 10 : 20,
        },
    });

export default MyRegistrations;